import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebaseConfig';

export const EVENT_TYPES = {
  ORDER_CREATED: 'ORDER_CREATED',
  STORE_ASSIGNED: 'STORE_ASSIGNED',
  ROUTE_OVERWRITTEN: 'ROUTE_OVERWRITTEN',
  ROUTED_TO_STORE: 'ROUTED_TO_STORE',
  STORE_ACCEPTED: 'STORE_ACCEPTED',
  STORE_CONFIRMED: 'STORE_CONFIRMED',
  SWAP_REQUESTED: 'SWAP_REQUESTED',
  SWAP_APPROVED: 'SWAP_APPROVED',
  PACKING_STARTED: 'PACKING_STARTED',
  PACKING_PROOF: 'PACKING_PROOF',
  READY_FOR_DRIVER: 'READY_FOR_DRIVER',
  DRIVER_REQUESTED: 'DRIVER_REQUESTED',
  DRIVER_ASSIGNED: 'DRIVER_ASSIGNED',
  DRIVER_ACCEPTED: 'DRIVER_ACCEPTED',
  COLLECTED: 'COLLECTED',
  OUT_FOR_DELIVERY: 'OUT_FOR_DELIVERY',
  DELIVERED: 'DELIVERED',
  RETURNED: 'RETURNED',
  CANCELLED: 'CANCELLED',
  ISSUE_FLAGGED: 'ISSUE_FLAGGED',
  CHAT_MESSAGE: 'CHAT_MESSAGE',
};

export const LEGACY_EVENT_TYPE_MAP = {
  intake_created: EVENT_TYPES.ORDER_CREATED,
  status_change: 'STATUS_CHANGE',
  issue_flagged: EVENT_TYPES.ISSUE_FLAGGED,
  driver_assigned: EVENT_TYPES.DRIVER_ASSIGNED,
  store_accepted: EVENT_TYPES.STORE_ACCEPTED,
  store_confirmed: EVENT_TYPES.STORE_CONFIRMED,
  store_swap_requested: EVENT_TYPES.SWAP_REQUESTED,
  site_swap_product_added: EVENT_TYPES.SWAP_REQUESTED,
  site_swap_product_removed: EVENT_TYPES.SWAP_REQUESTED,
  site_item_unavailable: EVENT_TYPES.SWAP_REQUESTED,
  site_item_reactivated: EVENT_TYPES.STORE_CONFIRMED,
  packing_started: EVENT_TYPES.PACKING_STARTED,
  ready_for_driver: EVENT_TYPES.READY_FOR_DRIVER,
  store_ready_for_driver: EVENT_TYPES.READY_FOR_DRIVER,
  store_packed_proof: EVENT_TYPES.PACKING_PROOF,
  store_collected_by_driver: EVENT_TYPES.COLLECTED,
  other_driver_completed_by_site: EVENT_TYPES.DELIVERED,
  site_moved_back_to_waiting: 'MOVED_BACK_TO_WAITING',
  driver_requested_run: EVENT_TYPES.DRIVER_REQUESTED,
  site_selected_driver: EVENT_TYPES.DRIVER_ASSIGNED,
  site_unselected_driver: 'DRIVER_UNASSIGNED',
  driver_start_run: EVENT_TYPES.OUT_FOR_DELIVERY,
  picked_up: EVENT_TYPES.COLLECTED,
  out_for_delivery: EVENT_TYPES.OUT_FOR_DELIVERY,
  delivered_with_proof: EVENT_TYPES.DELIVERED,
  delivered: EVENT_TYPES.DELIVERED,
  site_cancelled_order: EVENT_TYPES.CANCELLED,
  link_chat_message: EVENT_TYPES.CHAT_MESSAGE,
};

export function normalizeOrderEventType(type = '', event = {}) {
  const raw = String(type || event?.type || '').trim();
  if (!raw) return 'ORDER_UPDATE';
  if (raw === 'status_change') {
    const to = String(event?.to || event?.stage || event?.meta?.to || '').trim();
    if (to === 'awaiting_store') return EVENT_TYPES.ROUTED_TO_STORE;
    if (to === 'ready_for_driver') return EVENT_TYPES.READY_FOR_DRIVER;
    if (to === 'out_for_delivery') return EVENT_TYPES.OUT_FOR_DELIVERY;
    if (to === 'delivered') return EVENT_TYPES.DELIVERED;
    if (to === 'cancelled') return EVENT_TYPES.CANCELLED;
    if (to === 'issue') return EVENT_TYPES.ISSUE_FLAGGED;
  }
  return LEGACY_EVENT_TYPE_MAP[raw] || raw.toUpperCase().replace(/[^A-Z0-9]+/g, '_');
}

export function actorLabel(actor = {}) {
  if (!actor) return 'System';
  return actor.name || actor.displayName || actor.email || actor.roleLabel || actor.role || 'Link user';
}

export function eventTitle(type = '') {
  const clean = String(type || 'ORDER_UPDATE').replace(/_/g, ' ').toLowerCase();
  return clean.replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export function normalizeOrderEvent(event = {}) {
  const type = normalizeOrderEventType(event.type, event);
  return {
    ...event,
    type,
    legacyType: event.legacyType || event.type || '',
    title: event.title || eventTitle(type),
    actorLabel: event.actorLabel || actorLabel(event.actor),
    message: event.message || eventTitle(type),
    meta: event.meta || {},
  };
}

export async function pushOrderEvent({ orgId, orderId, orderNumber = '', type, actor = null, message = '', meta = {} }) {
  if (!orgId || !orderId || !type) return;
  const event = normalizeOrderEvent({ type, actor, message, meta });
  await addDoc(collection(db, 'orgs', orgId, 'ontimeOrders', orderId, 'events'), {
    orgId,
    orderId,
    orderNumber,
    ...event,
    createdAt: serverTimestamp(),
  });
}
